import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  EventEmitter,
  inject,
  Input,
  OnInit,
  Output,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { CdkDragDrop, moveItemInArray } from '@angular/cdk/drag-drop';
import {
  catchError,
  concatMap,
  EMPTY,
  filter,
  finalize,
  forkJoin,
  from,
  last,
  map,
  Observable,
  of,
  switchMap,
  tap,
} from 'rxjs';
import { TranslationManagerService } from '../../../core/i18n';
import { VCHASNO_TAX_GROUP_OPTIONS } from '../../prro/prro.types';
import { CURRENCY_TYPE } from '../../../enum/constants';
import { WarehouseService } from '../../../services/warehouse.service';
import { FileSystemService } from '../../../services/file-system.service';
import { Upload } from '../../../modals/file-upload-new/file-upload-new.interface';
import {
  ProductPayload,
  StockTotals,
  VariantPayload,
  Warehouse,
  WarehouseProduct,
  WarehouseProperty,
  WarehouseUnit,
  WarehouseVariant,
} from '../warehouse.types';
import {
  CategoryNode,
  CURRENCY_OPTIONS,
  escapeHtml,
  formatQuantity,
  getCategoryTree,
  getCurrencySymbol,
  getFileUrl,
  parseNumber,
} from '../warehouse.utils';
import { formatMoneyAmount } from '../../board-v3/price-field.utils';
import { SelectType } from '../../select/select.interface';
import { TableV2Action, TableV2Column } from '../../table-v2/table-v2.interface';

type RowField = 'sku' | 'barcode' | 'price' | 'values' | 'quantity' | 'purchase_price';

/** A variant as it is edited: the typed numbers stay text */
interface VariantRow {
  /** The variant on the server, null for a new one */
  variant: WarehouseVariant | null;
  sku: string;
  barcode: string;
  price: string;
  photo_id: string | null;
  is_archived: boolean;
  /** property id -> value id */
  values: Record<string, string | null>;
  /** Payload of the saved variant, so only what changed is sent */
  saved: string | null;
  /** Opening stock of a new variant, received by a document once the product is saved */
  quantity: string;
  purchase_price: string;
  invalid: Set<RowField>;
}

/** A receipt or write-off of a saved variant, posted right from its row */
interface QuickStock {
  row: VariantRow;
  type: 'RECEIPT' | 'WRITE_OFF';
  warehouse_id: string | null;
  quantity: string;
  price: string;
  reason: string;
  /** The variant stock on every warehouse */
  balances: Record<string, StockTotals>;
  invalid: Set<'warehouse_id' | 'quantity' | 'price'>;
  saving: boolean;
}

const MAX_GENERATED_VARIANTS = 500;

/** Property values of a variant in any column order; the backend keeps them unique within a product */
const valuesKey = (ids: (string | null)[]): string => ids.filter(Boolean).sort().join('|');

/**
 * Product of the warehouse catalog with its variants, photos and variant properties.
 * A new product is created with all variants at once; an existing one saves the product fields,
 * then the changed variants, then the new ones, each request returning the whole product.
 * New variants can get their opening stock right away: it is posted as a receipt after the product is saved.
 */
@Component({
  selector: 'app-warehouse-product-form',
  templateUrl: './product-form.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
  host: { class: 'block h-full min-h-0' },
})
export class WarehouseProductFormComponent implements OnInit {
  @Input() productId: string | null = null;
  /** Category of a new product */
  @Input() categoryId: string | null = null;
  /** Whether the product changed */
  @Output() close = new EventEmitter<boolean>();

  private readonly warehouseService = inject(WarehouseService);
  private readonly fileSystemService = inject(FileSystemService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly translations = inject(TranslationManagerService);
  protected readonly taxOptions = VCHASNO_TAX_GROUP_OPTIONS;

  protected readonly form = new FormGroup({
    name: new FormControl('', { nonNullable: true, validators: [Validators.required, Validators.maxLength(255)] }),
    category_id: new FormControl<string | null>(null),
    tax_group: new FormControl<number | null>(null, [Validators.min(1), Validators.max(9)]),
    unit_id: new FormControl<string | null>(null, [Validators.required]),
    currency: new FormControl<CURRENCY_TYPE>(CURRENCY_TYPE.UAH, { nonNullable: true }),
    description: new FormControl<string | null>('', [Validators.maxLength(10000)]),
  });

  protected product: WarehouseProduct | null = null;
  protected units: WarehouseUnit[] = [];
  protected categories: CategoryNode[] = [];
  protected properties: WarehouseProperty[] = [];
  /** Active warehouses for the opening and quick stock */
  protected warehouses: Warehouse[] = [];
  protected photoIds: string[] = [];
  protected propertyIds: string[] = [];
  protected rows: VariantRow[] = [];
  protected errorMessages: string[] = [];
  /** The opening stock of the new variants is received on it */
  protected stockWarehouseId: string | null = null;
  protected stockWarehouseError = false;
  protected quickStock: QuickStock | null = null;

  protected loading = true;
  protected loadFailed = false;
  protected saving = false;
  protected busy = false;
  protected uploading = 0;
  /** The product changed on the server (archived, a variant deleted, stock moved) */
  private changed = false;
  private savedProduct = '{}';

  // Properties panel
  protected newPropertyName = '';
  protected newValueTexts: Record<string, string> = {};
  protected generatorOpen = false;
  protected generatorValues: Record<string, string[]> = {};

  protected readonly currencyOptions = CURRENCY_OPTIONS;
  protected readonly formatMoneyAmount = formatMoneyAmount;
  protected readonly formatQuantity = formatQuantity;
  protected readonly getFileUrl = getFileUrl;
  protected readonly SelectType = SelectType;
  protected readonly variantActions: TableV2Action<VariantRow>[] = [
    ...(['RECEIPT', 'WRITE_OFF'] as const).map((type): TableV2Action<VariantRow> => ({
      icon: type === 'RECEIPT' ? 'add_circle' : 'do_not_disturb_on',
      tooltip: `stock.product_form.quick_stock.${type === 'RECEIPT' ? 'receipt_hint' : 'write_off_hint'}`,
      tone: type === 'RECEIPT' ? 'success' : 'danger',
      visible: (row) => !!row.variant && !row.is_archived,
      active: (row) => this.quickStock?.row === row && this.quickStock.type === type,
      click: (row) => this.toggleQuickStock(row, type),
    })),
    {
      icon: (row) => (row.is_archived ? 'unarchive' : 'archive'),
      tooltip: (row) => (row.is_archived ? 'restore' : 'archive'),
      visible: (row) => !!row.variant,
      click: (row): void => {
        row.is_archived = !row.is_archived;
        if (this.quickStock?.row === row && !this.quickStock.saving) this.quickStock = null;
      },
    },
    {
      icon: 'delete',
      tooltip: 'delete',
      tone: 'danger',
      disabled: () => this.busy,
      click: (row) => this.removeRow(row),
    },
  ];
  protected readonly archivedRowClass = (row: VariantRow): string => (row.is_archived ? 'opacity-60' : '');
  private variantColumnsCache: { key: string; columns: TableV2Column<VariantRow>[] } | null = null;

  /** Until the product is created: after that the form edits it */
  get isNew(): boolean {
    return !this.productId && !this.product;
  }

  /** The opening stock the receipt takes: the valid typed rows only */
  get openingStock(): { row: VariantRow; quantity: number; price: number }[] {
    return this.rows.flatMap((row) => {
      const quantity = parseNumber(row.quantity, 3);
      const price = parseNumber(row.purchase_price, 4);
      return !row.variant && !row.is_archived && quantity && price !== null ? [{ row, quantity, price }] : [];
    });
  }

  /** Property columns follow the chosen properties; cached, as a new array re-renders the table */
  get variantColumns(): TableV2Column<VariantRow>[] {
    const properties = this.chosenProperties;
    const key = properties.map((property) => `${property.id}:${property.name}`).join('|');
    if (this.variantColumnsCache?.key === key) return this.variantColumnsCache.columns;

    const number = { align: 'right', nowrap: true, class: 'w-32' } as const;
    const columns: TableV2Column<VariantRow>[] = [
      { key: 'photo', header: 'photo', class: 'w-14' },
      ...properties.map((property) => ({ key: `property:${property.id}`, label: property.name, class: 'min-w-36' })),
      { key: 'sku', header: 'stock.sku', class: 'min-w-28' },
      { key: 'barcode', header: 'stock.barcode', class: 'min-w-32' },
      { key: 'purchase', ...number, cellClass: 'text-slate-500' },
      { key: 'price', ...number },
      { key: 'stock', ...number, class: 'w-28' },
    ];
    this.variantColumnsCache = { key, columns };
    return columns;
  }

  get chosenProperties(): WarehouseProperty[] {
    return this.properties.filter((property) => this.propertyIds.includes(property.id));
  }

  get otherProperties(): WarehouseProperty[] {
    return this.properties.filter((property) => !this.propertyIds.includes(property.id));
  }

  get hasNewRows(): boolean {
    return this.rows.some((row) => !row.variant);
  }

  get openingTotal(): { quantity: number; amount: number } {
    return this.openingStock.reduce(
      (total, { quantity, price }) => ({
        quantity: total.quantity + quantity,
        amount: total.amount + quantity * price,
      }),
      { quantity: 0, amount: 0 },
    );
  }

  get currencySymbol(): string {
    return getCurrencySymbol(this.form.controls.currency.value);
  }

  get unitShortName(): string {
    return this.units.find((unit) => unit.id === this.form.controls.unit_id.value)?.short_name ?? '';
  }

  get generatedCount(): number {
    return this.chosenProperties.reduce(
      (count, property) => count * (this.generatorValues[property.id]?.length ?? 0),
      1,
    );
  }

  get hasChanges(): boolean {
    if (this.isNew)
      return this.form.dirty || this.photoIds.length > 0 || this.rows.some((row) => this.isRowFilled(row));

    return Object.keys(this.getProductChanges()).length > 0 || this.rows.some((row) => this.isRowChanged(row));
  }

  ngOnInit(): void {
    this.translations.loadModuleTranslations('prro').pipe(takeUntilDestroyed(this.destroyRef)).subscribe();
    this.load();
  }

  protected load(): void {
    this.loading = true;
    this.loadFailed = false;
    forkJoin({
      units: this.warehouseService.getUnits(),
      categories: this.warehouseService.getCategories(),
      properties: this.warehouseService.getProperties(),
      // Without warehouses the product is still saved, only the opening stock is not offered
      warehouses: this.warehouseService.getWarehouses(false).pipe(catchError(() => of([]))),
      product: this.productId ? this.warehouseService.getProduct(this.productId) : of(null),
    })
      .pipe(
        finalize(() => (this.loading = false)),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe({
        next: ({ units, categories, properties, warehouses, product }) => {
          this.units = units;
          this.categories = getCategoryTree(categories);
          this.properties = properties;
          this.warehouses = warehouses;
          this.stockWarehouseId = warehouses[0]?.id ?? null;
          if (product) return this.applyProduct(product);

          const unit = units.find((item) => item.short_name === 'шт') ?? units[0];
          this.form.reset({ category_id: this.categoryId, unit_id: unit?.id ?? null });
          this.addRow();
        },
        error: (error) => {
          this.loadFailed = true;
          this.warehouseService.error(error);
        },
      });
  }

  // #region variants

  protected addRow(values: Record<string, string | null> = {}): void {
    this.rows = [...this.rows, this.createRow(null, values)];
  }

  protected setRowValue(row: VariantRow, propertyId: string, valueId: string | null): void {
    row.values = { ...row.values, [propertyId]: valueId };
    row.invalid.delete('values');
  }

  private removeRow(row: VariantRow): void {
    const drop = (): void => {
      this.rows = this.rows.filter((item) => item !== row);
      if (!this.rows.length) this.addRow();
    };
    if (!row.variant) return drop();

    this.busy = true;
    const { id, name } = row.variant;
    this.warehouseService
      .deleteOrArchive(
        'stock.product_form.variant_',
        name || row.sku,
        this.warehouseService.remove('variant', id),
        this.warehouseService.updateVariant(id, { is_archived: true }),
      )
      .pipe(
        finalize(() => (this.busy = false)),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe({
        next: (deleted) => {
          this.changed = true;
          if (deleted) return drop();

          // Only the archive flag reached the server, other edits of the row are still to be saved
          row.is_archived = true;
          row.saved = JSON.stringify({ ...(JSON.parse(row.saved ?? '{}') as VariantPayload), is_archived: true });
        },
        error: (error) => this.warehouseService.error(error),
      });
  }

  /** The first warehouse can be created right here, the new stock then goes on it */
  protected createWarehouse(): void {
    this.warehouseService.openWarehouseForm().subscribe((warehouse) => {
      if (!warehouse) return;

      this.warehouses = [...this.warehouses, warehouse];
      this.stockWarehouseId = warehouse.id;
      if (this.quickStock) this.quickStock.warehouse_id ??= warehouse.id;
    });
  }

  // #endregion

  // #region quick stock

  /** Opens the receipt / write-off of a saved variant under its row; the same button closes it */
  private toggleQuickStock(row: VariantRow, type: QuickStock['type']): void {
    if (this.quickStock?.saving) return;
    if (this.quickStock?.row === row && this.quickStock.type === type) {
      this.quickStock = null;
      return;
    }

    const isReceipt = type === 'RECEIPT';
    const action: QuickStock = {
      row,
      type,
      warehouse_id: isReceipt ? this.stockWarehouseId : null,
      quantity: '',
      // The last cost is the usual purchase price of the next delivery
      price: isReceipt && row.variant?.cost_price ? String(row.variant.cost_price) : '',
      reason: '',
      balances: {},
      invalid: new Set(),
      saving: false,
    };
    this.quickStock = action;
    this.warehouseService
      .getStock({ variant_id: row.variant?.id, take: 100 })
      .pipe(
        catchError(() => of({ items: [] })),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(({ items }) => {
        action.balances = Object.fromEntries(items.map((item) => [item.warehouse_id, item]));
        // A write-off takes from the active warehouse with most of the variant
        const active = items.filter((item) => this.warehouses.some((warehouse) => warehouse.id === item.warehouse_id));
        const fullest = active.sort((a, b) => b.available - a.available)[0];
        action.warehouse_id ??= (fullest?.available ?? 0) > 0 ? fullest.warehouse_id : this.stockWarehouseId;
      });
  }

  /** Posts the document at once and refreshes the stock and cost of the variants, leaving unsaved edits alone */
  protected submitQuickStock(action: QuickStock): void {
    const product = this.product;
    const isReceipt = action.type === 'RECEIPT';
    const quantity = parseNumber(action.quantity, 3);
    const price = parseNumber(action.price, 4);
    action.invalid = new Set([
      ...(action.warehouse_id ? [] : (['warehouse_id'] as const)),
      ...(quantity ? [] : (['quantity'] as const)),
      ...(isReceipt && price === null ? (['price'] as const) : []),
    ]);
    if (action.invalid.size || action.saving || !product || !action.row.variant) return;

    const warehouse = this.warehouses.find((item) => item.id === action.warehouse_id);
    action.saving = true;
    this.warehouseService
      .postNewDocument({
        type: action.type,
        warehouse_id: action.warehouse_id,
        currency: product.currency,
        reason: action.reason.trim() || null,
        lines: [
          { variant_id: action.row.variant.id, quantity: quantity ?? 0, ...(isReceipt && { price: price ?? 0 }) },
        ],
      })
      .pipe(
        switchMap(() => this.warehouseService.getProduct(product.id).pipe(catchError(() => of(null)))),
        finalize(() => (action.saving = false)),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe({
        next: (updated) => {
          this.changed = true;
          // Only the stock and cost price of the rows: the rest of the form may hold unsaved edits
          this.rows.forEach(
            (row) => (row.variant = updated?.variants.find(({ id }) => id === row.variant?.id) ?? row.variant),
          );
          if (this.quickStock === action) this.quickStock = null;
          this.warehouseService.success(`stock.product_form.quick_stock.${isReceipt ? 'received' : 'written_off'}`, {
            quantity: formatQuantity(quantity),
            unit: this.unitShortName,
            warehouse: escapeHtml(warehouse?.name),
          });
        },
        error: (error) => this.warehouseService.error(error),
      });
  }

  // #endregion

  // #region properties

  protected addPropertyColumn(property: WarehouseProperty): void {
    if (this.propertyIds.includes(property.id)) return;

    this.propertyIds = [...this.propertyIds, property.id];
    this.rows.forEach((row) => (row.values = { ...row.values, [property.id]: null }));
  }

  protected removePropertyColumn(property: WarehouseProperty): void {
    this.propertyIds = this.propertyIds.filter((id) => id !== property.id);
    this.rows.forEach((row) => {
      row.values = { ...row.values };
      delete row.values[property.id];
      row.invalid.clear();
    });
    delete this.generatorValues[property.id];
    this.errorMessages = [];
  }

  /** Adds the property of the typed name, creating it when there is none */
  protected createProperty(): void {
    const name = this.newPropertyName.trim();
    const existing = this.properties.find((property) => property.name.toLowerCase() === name.toLowerCase());
    if (!name || this.busy) return;
    if (existing) {
      this.addPropertyColumn(existing);
      this.newPropertyName = '';
      return;
    }

    this.runBusy(this.warehouseService.createProperty({ name }), (property) => {
      this.properties = [...this.properties, property];
      this.addPropertyColumn(property);
      this.newPropertyName = '';
    });
  }

  protected addPropertyValue(property: WarehouseProperty): void {
    const value = (this.newValueTexts[property.id] ?? '').trim();
    if (!value || this.busy) return;

    this.runBusy(this.warehouseService.addPropertyValue(property.id, value), (updated) => {
      this.properties = this.properties.map((item) => (item.id === updated.id ? updated : item));
      this.newValueTexts[property.id] = '';
    });
  }

  /** Adds a variant for every combination of the picked values that the product does not have yet */
  protected generateVariants(): void {
    if (this.rows.length + this.generatedCount > MAX_GENERATED_VARIANTS) {
      this.warehouseService.fail('stock.product_form.too_many_variants', { max: MAX_GENERATED_VARIANTS });
      return;
    }

    const combinations = this.chosenProperties.reduce<Record<string, string>[]>(
      (result, property) =>
        result.flatMap((combination) =>
          (this.generatorValues[property.id] ?? []).map((valueId) => ({ ...combination, [property.id]: valueId })),
        ),
      [{}],
    );
    const existing = new Set(this.rows.map((row) => valuesKey(Object.values(row.values))));
    const fresh = combinations.filter((combination) => !existing.has(valuesKey(Object.values(combination))));

    // The empty starting row of a new product is replaced, not kept next to the generated ones
    this.rows = this.rows.filter((row) => row.variant || this.isRowFilled(row));
    fresh.forEach((values) => this.addRow(values));
    if (!this.rows.length) this.addRow();
    this.generatorOpen = false;
    this.generatorValues = {};
    this.warehouseService.success('stock.product_form.variants_generated', { count: fresh.length });
  }

  // #endregion

  // #region photos

  /** Photos of the product, or the own photo of `row` */
  protected onPhotosPicked(input: HTMLInputElement, row?: VariantRow): void {
    const files = Array.from(input.files ?? []);
    input.value = '';
    files.forEach((file) => {
      if (!file.type.startsWith('image/')) {
        this.warehouseService.fail('stock.product_form.photo_not_image');
        return;
      }

      this.uploading += 1;
      (this.fileSystemService.uploadFile(file) as Observable<Upload & { body?: { data?: { id: string } } }>)
        .pipe(
          filter((upload) => upload.state),
          last(),
          map((upload) => upload.body?.data?.id),
          filter((fileId): fileId is string => !!fileId),
          finalize(() => (this.uploading -= 1)),
          takeUntilDestroyed(this.destroyRef),
        )
        .subscribe({
          next: (fileId) => {
            if (row) row.photo_id = fileId;
            else this.photoIds = [...this.photoIds, fileId];
          },
          error: (error) => this.warehouseService.error(error),
        });
    });
  }

  protected dropProductPhoto(event: CdkDragDrop<unknown>): void {
    const photoIds = [...this.photoIds];
    moveItemInArray(photoIds, event.previousIndex, event.currentIndex);
    this.photoIds = photoIds;
  }

  // #endregion

  // #region product actions

  protected toggleArchive(product: WarehouseProduct): void {
    this.runBusy(this.warehouseService.archiveProduct(product, !product.is_archived), (updated) => {
      this.product = updated;
      this.changed = true;
    });
  }

  protected deleteProduct(product: WarehouseProduct): void {
    this.runBusy(this.warehouseService.deleteProduct(product), () => this.close.emit(true));
  }

  // #endregion

  // #region save and close

  protected save(): void {
    this.form.markAllAsTouched();
    if (this.saving || this.uploading || !this.validate() || this.form.invalid) return;

    const opening = this.openingStock;
    this.saving = true;
    (this.isNew ? this.createProduct() : this.updateProduct())
      .pipe(
        switchMap((product) =>
          this.receiveOpeningStock(product, opening).pipe(
            catchError((error: unknown) => {
              // The product is saved: the form now edits it, its stock can be received from the variant rows
              this.changed = true;
              this.applyProduct(product);
              this.warehouseService.error(error, undefined, 'stock.product_form.stock_failed');
              return EMPTY;
            }),
          ),
        ),
        finalize(() => (this.saving = false)),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe({
        next: (warehouse) => {
          const key = warehouse ? 'stock.product_form.saved_with_stock' : 'stock.product_form.saved';
          this.warehouseService.success(key, { warehouse: escapeHtml(warehouse?.name) });
          this.close.emit(true);
        },
        error: (error) => this.warehouseService.error(error),
      });
  }

  /** Called by the sidebar close button: unsaved edits ask first */
  public requestClose(): void {
    if (this.saving) return;

    this.warehouseService
      .confirm(this.hasChanges ? 'stock.unsaved_confirm' : null)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.close.emit(this.changed));
  }

  private createProduct(): Observable<WarehouseProduct> {
    const variants = this.rows.map((row) => this.buildRowPayload(row));
    return this.warehouseService.saveProduct(null, { ...this.buildProductPayload(), variants });
  }

  /**
   * Product fields first, then the changed variants (so a SKU moved to a new variant is free), then the new ones.
   * Every finished step stays applied, so after an error a retry sends only what is left.
   */
  private updateProduct(): Observable<WarehouseProduct> {
    const product = this.product!;
    const changes = this.getProductChanges();
    const payload = JSON.stringify(this.buildProductPayload());
    const steps: (() => Observable<WarehouseProduct>)[] = [
      ...(Object.keys(changes).length
        ? [
            (): Observable<WarehouseProduct> =>
              this.warehouseService.saveProduct(product.id, changes).pipe(tap(() => (this.savedProduct = payload))),
          ]
        : []),
      ...this.rows.filter((row) => row.variant && this.isRowChanged(row)).map((row) => () => this.saveRow(row)),
      ...this.rows.filter((row) => !row.variant).map((row) => () => this.saveRow(row)),
    ];

    return from(steps).pipe(
      concatMap((step) => step()),
      tap((updated) => {
        this.product = updated;
        this.changed = true;
      }),
      last(null, product),
    );
  }

  /** Creates the variant or saves its changes; the product comes back with all its variants */
  private saveRow(row: VariantRow): Observable<WarehouseProduct> {
    const payload = this.buildRowPayload(row);
    const known = new Set(this.rows.map((item) => item.variant?.id));
    const request$ = row.variant
      ? this.warehouseService.updateVariant(row.variant.id, payload)
      : this.warehouseService.createVariant(this.product!.id, payload);

    return request$.pipe(
      tap((product) => {
        row.variant = product.variants.find(({ id }) => (row.variant ? id === row.variant.id : !known.has(id))) ?? null;
        row.saved = JSON.stringify(payload);
      }),
    );
  }

  /** One receipt with the opening stock of the new variants, at their purchase prices in the product currency */
  private receiveOpeningStock(
    product: WarehouseProduct,
    opening: { row: VariantRow; quantity: number; price: number }[],
  ): Observable<Warehouse | null> {
    // Variants of a product created just now are found by their property values
    const created = new Map(
      product.variants.map((variant) => [
        valuesKey(variant.properties.map((item) => item.property_value_id)),
        variant.id,
      ]),
    );
    const lines = opening.flatMap(({ row, quantity, price }) => {
      const variantId = row.variant?.id ?? created.get(valuesKey(Object.values(row.values)));
      return variantId ? [{ variant_id: variantId, quantity, price }] : [];
    });
    const warehouse = this.warehouses.find((item) => item.id === this.stockWarehouseId);
    if (!lines.length || !warehouse) return of(null);

    return this.warehouseService
      .postNewDocument({
        type: 'RECEIPT',
        warehouse_id: warehouse.id,
        currency: product.currency,
        reason: this.warehouseService.t('stock.product_form.initial_stock_reason', { name: product.name }),
        lines,
      })
      .pipe(map(() => warehouse));
  }

  // #endregion

  // #region form state

  private applyProduct(product: WarehouseProduct): void {
    this.product = product;
    this.form.reset({ ...product, tax_group: product.tax_group ?? null, description: product.description ?? '' });
    // Photos deleted from the file system are not in `photos`: sending their ids again fails with FILE_NOT_FOUND
    this.photoIds = product.photos.map((photo) => photo.id);
    // Columns in the order of the property list
    const used = new Set(product.variants.flatMap((variant) => variant.properties.map((item) => item.property_id)));
    this.propertyIds = this.properties.filter((property) => used.has(property.id)).map((property) => property.id);
    this.rows = product.variants.map((variant) => this.createRow(variant));
    if (!this.rows.length) this.addRow();
    this.savedProduct = JSON.stringify(this.buildProductPayload());
  }

  private createRow(variant: WarehouseVariant | null, values: Record<string, string | null> = {}): VariantRow {
    const row: VariantRow = {
      variant,
      sku: variant?.sku ?? '',
      barcode: variant?.barcode ?? '',
      price: variant ? String(variant.price) : '',
      photo_id: variant?.photo_id ?? null,
      is_archived: variant?.is_archived ?? false,
      values: {
        ...Object.fromEntries(this.propertyIds.map((id) => [id, null])),
        ...values,
        ...Object.fromEntries(variant?.properties.map((item) => [item.property_id, item.property_value_id]) ?? []),
      },
      saved: null,
      quantity: '',
      purchase_price: '',
      invalid: new Set(),
    };
    if (variant) row.saved = JSON.stringify(this.buildRowPayload(row));
    return row;
  }

  private buildProductPayload(): ProductPayload {
    const raw = this.form.getRawValue();
    return {
      ...raw,
      name: raw.name.trim(),
      unit_id: raw.unit_id ?? undefined,
      description: raw.description?.trim() || null,
      photo_ids: this.photoIds,
    };
  }

  /** The product fields that differ from the server: the backend checks every sent photo and locks the currency */
  private getProductChanges(): ProductPayload {
    const saved = JSON.parse(this.savedProduct) as Record<string, unknown>;
    return Object.fromEntries(
      Object.entries(this.buildProductPayload()).filter(
        ([key, value]) => JSON.stringify(value) !== JSON.stringify(saved[key]),
      ),
    );
  }

  private buildRowPayload(row: VariantRow): VariantPayload {
    return {
      sku: row.sku.trim() || null,
      barcode: row.barcode.trim() || null,
      price: parseNumber(row.price, 4) ?? 0,
      photo_id: row.photo_id,
      property_value_ids: this.propertyIds.map((id) => row.values[id]).filter((id): id is string => !!id),
      is_archived: row.is_archived,
    };
  }

  private isRowChanged(row: VariantRow): boolean {
    return JSON.stringify(this.buildRowPayload(row)) !== row.saved;
  }

  private isRowFilled(row: VariantRow): boolean {
    const texts = [row.sku, row.barcode, row.price, row.quantity, row.purchase_price];
    return texts.some((text) => text.trim()) || !!row.photo_id || Object.values(row.values).some(Boolean);
  }

  /** The checks the backend would answer with 400 / 409 for this product alone */
  private validate(): boolean {
    const messages = new Set<string>();
    const mark = (row: VariantRow, field: RowField, key: string): void => {
      row.invalid.add(field);
      messages.add(this.warehouseService.t(`stock.product_form.${key}`));
    };
    const duplicates = (field: RowField, key: string, value: (row: VariantRow) => string): void => {
      const groups = new Map<string, VariantRow[]>();
      this.rows.forEach((row) => groups.set(value(row), [...(groups.get(value(row)) ?? []), row]));
      groups.forEach((rows, group) => {
        if (rows.length > 1 && (group || field === 'values')) rows.forEach((row) => mark(row, field, key));
      });
    };

    this.rows.forEach((row) => {
      row.invalid = new Set();
      if (row.price.trim() && parseNumber(row.price, 4) === null) mark(row, 'price', 'error_price');
      // Opening stock: nothing, or a positive quantity with a purchase price; a purchase price alone has nowhere to go
      const quantity = parseNumber(row.quantity, 3);
      const noQuantity = !row.quantity.trim() || quantity === 0;
      if (row.variant || row.is_archived || (noQuantity && !row.purchase_price.trim())) return;

      if (!quantity) mark(row, 'quantity', noQuantity ? 'error_quantity_required' : 'error_quantity');
      if (parseNumber(row.purchase_price, 4) === null) {
        mark(row, 'purchase_price', row.purchase_price.trim() ? 'error_price' : 'error_purchase_price_required');
      }
    });
    duplicates('sku', 'error_sku_duplicate', (row) => row.sku.trim().toLowerCase());
    duplicates('barcode', 'error_barcode_duplicate', (row) => row.barcode.trim());
    const combinationKey = this.propertyIds.length ? 'error_combination_duplicate' : 'error_need_properties';
    duplicates('values', combinationKey, (row) => valuesKey(Object.values(row.values)));

    this.stockWarehouseError = !this.stockWarehouseId && this.openingStock.length > 0;
    this.errorMessages = [...messages];
    return !messages.size && !this.stockWarehouseError;
  }

  // #endregion

  /** A request of the properties panel or the product actions, one at a time */
  private runBusy<T>(request$: Observable<T>, done: (result: T) => void): void {
    this.busy = true;
    request$
      .pipe(
        finalize(() => (this.busy = false)),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe({ next: done, error: (error) => this.warehouseService.error(error) });
  }
}
